import React, { useEffect, useState, useMemo } from "react";
import { getAllGuard, deleteGuard } from "../api/vipform";
import { useGuardStore } from "../context/GuardContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

export default function GuardDetails() {
  const { handleEdit, refreshTrigger } = useGuardStore();
  const [guardList, setGuardList] = useState([]);
  const navigate = useNavigate();

  const [selectedRank, setSelectedRank] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [searchText, setSearchText] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const role = localStorage.getItem("role");

  /* FETCH GUARDS */
  useEffect(() => {
    async function fetchGuards() {
      try {
        const res = await getAllGuard(0, 10000);
        setGuardList(res.content || []);
      } catch (err) {
        toast.error("Failed to load guards!");
      }
    }
    fetchGuards();
  }, [refreshTrigger]);

  const handleRowClick = (guard) => {
    if (guard.status === "Active") {
      navigate(`/officerassignedduty/${guard.id}`);
    } else {
      navigate(`/officerdutyhistory/${guard.id}`);
    }
  };

  /* DELETE */
  const handleDelete = async (g) => {
    try {
      await deleteGuard(g.id);
      window.alert("Guard deleted successfully!");
      setCurrentPage(0);
    } catch (err) {
      toast.error("Failed to delete guard!");
    }
  };

  /* SORT */
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  /* FILTERING */
  const filteredGuards = useMemo(() => {
    return guardList.filter((g) => {
      const validStatus = g.status.toLowerCase() === "active" || g.status.toLowerCase() === "inactive";
      if (!validStatus) return false;

      const matchStatus = selectedStatus ? g.status === selectedStatus : true;
      const matchRank = selectedRank ? g.rank === selectedRank : true;
      const matchSearch = searchText
        ? Object.values(g).join(" ").toLowerCase().includes(searchText.toLowerCase())
        : true;

      return matchStatus && matchRank && matchSearch;
    });
  }, [guardList, selectedStatus, selectedRank, searchText]);

  /* SORTING */
  const sortedGuards = useMemo(() => {
    let data = [...filteredGuards];

    if (sortConfig.key) {
      data.sort((a, b) => {
        const valA = a[sortConfig.key] ?? "";
        const valB = b[sortConfig.key] ?? "";

        if (typeof valA === "number" && typeof valB === "number") {
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        }

        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
      return data;
    }

    const statusOrder = { Inactive: 0, Active: 1, Deleted: 2 };
    return data.sort((a, b) => {
      const aOrder = statusOrder[a.status] ?? 3;
      const bOrder = statusOrder[b.status] ?? 3;
      return aOrder - bOrder;
    });
  }, [filteredGuards, sortConfig]);

  /* PAGINATION */
  const totalPages = Math.ceil(sortedGuards.length / rowsPerPage);
  const paginatedGuards = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return sortedGuards.slice(start, start + rowsPerPage);
  }, [sortedGuards, currentPage, rowsPerPage]);

  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) setCurrentPage(page);
  };

  const getVisiblePages = () => [...Array(totalPages)].map((_, i) => i);

  /* RANKS */
  const ranks = [...new Set(guardList.map((g) => g.rank || "Uncategorized"))];

  /* SORT ICON */
  const sortIcon = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc"
        ? " ↑"
        : " ↓"
      : "";

  return (
    <>
      <div className="guarddetails-wrapper">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">
            <h1 className="page-title">Officer Records</h1>
            <div className="total-card">
              <span className="total-count">{filteredGuards.length}</span>
              <span className="total-label">Total Officers</span>
            </div>
          </div>
        </header>

        {/* Filters */}
        <section className="filters-section">
          <div className="filter-group">
            <label>Rank</label>
            <select
              value={selectedRank}
              onChange={(e) => {
                setSelectedRank(e.target.value);
                setCurrentPage(0);
              }}
            >
              <option value="">All Ranks</option>
              {ranks.map((r, i) => (
                <option key={i} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setCurrentPage(0);
              }}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Name, Email, Contact, ID..."
              />
            </div>
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {paginatedGuards.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-user-shield"></i>
              <h3>No Officer Records Found</h3>
              <p>Try adjusting filters or add a new officer.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th onClick={() => handleSort("name")} className="sortable">
                        Name{sortIcon("name")}
                      </th>
                      <th onClick={() => handleSort("id")} className="sortable">
                        ID{sortIcon("id")}
                      </th>
                      <th onClick={() => handleSort("email")} className="sortable">
                        Email{sortIcon("email")}
                      </th>
                      <th onClick={() => handleSort("rank")} className="sortable">
                        Rank{sortIcon("rank")}
                      </th>
                      <th onClick={() => handleSort("experience")} className="sortable">
                        Experience{sortIcon("experience")}
                      </th>
                      <th onClick={() => handleSort("contactno")} className="sortable">
                        Contact{sortIcon("contactno")}
                      </th>
                      <th onClick={() => handleSort("status")} className="sortable">
                        Status{sortIcon("status")}
                      </th>
                      {role !== "user" && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedGuards.map((g, i) => (
                      <tr
                        key={g.id}
                        className="table-row"
                        onClick={() => handleRowClick(g)}
                      >
                        <td>{i + 1 + currentPage * rowsPerPage}</td>
                        <td className="name-cell">{g.name}</td>
                        <td>{g.id}</td>
                        <td>{g.email}</td>
                        <td>
                          <span className="badge">{g.rank || "—"}</span>
                        </td>
                        <td>{g.experience ? `${g.experience} Years` : "—"}</td>
                        <td>{g.contactno}</td>
                        <td>
                          <span className={`status-badge ${g.status.toLowerCase()}`}>
                            {g.status}
                          </span>
                        </td>
                        {role !== "user" && (
                          <td className="actions-cell">
                            <button
                              className="action-btn edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(g);
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(g);
                              }}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {paginatedGuards.map((g, i) => (
                  <div
                    key={g.id}
                    className="guard-card"
                    onClick={() => handleRowClick(g)}
                  >
                    <div className="card-header">
                      <span className="index">#{i + 1 + currentPage * rowsPerPage}</span>
                      <span className={`status-badge ${g.status.toLowerCase()}`}>
                        {g.status}
                      </span>
                    </div>
                    <h3 className="guard-name">{g.name}</h3>
                    <div className="card-details">
                      <div><strong>ID:</strong> {g.id}</div>
                      <div><strong>Email:</strong> {g.email}</div>
                      <div><strong>Rank:</strong> <span className="badge">{g.rank || "—"}</span></div>
                      <div><strong>Experience:</strong> {g.experience ? `${g.experience} Years` : "—"}</div>
                      <div><strong>Contact:</strong> {g.contactno}</div>
                    </div>
                    {role !== "user" && (
                      <div className="card-actions">
                        <button
                          className="action-btn edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(g);
                          }}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(g);
                          }}
                        >
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 0}
                className="page-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {getVisiblePages().map((i) => (
                <button
                  key={i}
                  onClick={() => goToPage(i)}
                  className={`page-btn ${currentPage === i ? "active" : ""}`}
                >
                  {i + 1}
                </button>
              ))}

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                className="page-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>

              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="rows-select"
              >
                <option value={20}>20 / page</option>
                <option value={30}>30 / page</option>
              </select>
            </div>
          )}
        </section>
      </div>

      <style jsx>{`
        .guarddetails-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .filters-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .filter-group {
          flex: 1;
          min-width: 220px;
          display: flex;
          flex-direction: column;
        }

        .filter-group label {
          font-weight: 600;
          margin-bottom: 8px;
          color: white;
        }

        .filter-group select,
        .search-input-wrapper input {
          padding: 14px 18px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          font-size: 1rem;
        }

        .filter-group select:focus,
        .search-input-wrapper input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input {
          padding-left: 48px;
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
          overflow: hidden;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .sortable {
          cursor: pointer;
          user-select: none;
        }

        .sortable:hover {
          background: rgba(59, 130, 246, 0.25);
        }

        .table-row {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.1);
          transform: translateY(-2px);
        }

        .table-row td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .name-cell {
          font-weight: 600;
          color: white;
        }

        .badge {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-badge.inactive {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .actions-cell {
          display: flex;
          gap: 12px;
        }

        .action-btn {
          padding: 10px 14px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.9rem;
        }

        .action-btn.edit {
          background: #10b981;
          color: white;
        }

        .action-btn.edit:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .action-btn.delete {
          background: #ef4444;
          color: white;
        }

        .action-btn.delete:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .guard-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .guard-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.15);
          background: rgba(15, 23, 42, 0.8);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .guard-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 12px;
          margin-top: 30px;
          flex-wrap: wrap;
        }

        .page-btn {
          width: 44px;
          height: 44px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .page-btn:hover:not(:disabled) {
          background: #3b82f6;
          transform: translateY(-2px);
        }

        .page-btn.active {
          background: #3b82f6;
          border-color: #3b82f6;
          font-weight: 600;
        }

        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .rows-select {
          padding: 12px 16px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.4);
          border-radius: 12px;
          color: white;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .filter-group {
            min-width: auto;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}

// import React, { useEffect, useState, useMemo } from "react";
// import { getAllGuard, deleteGuard } from "../api/vipform";
// import { useGuardStore } from "../context/GuardContext";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";

// export default function GuardDetails() {
//   const { handleEdit, refreshTrigger } = useGuardStore();

//   const [guardList, setGuardList] = useState([]);
//   const navigate = useNavigate();

//   const [selectedRank, setSelectedRank] = useState("");
//   const [selectedStatus, setSelectedStatus] = useState("");
//   const [searchText, setSearchText] = useState("");

//   const [currentPage, setCurrentPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(20);

//   // 🔹 SORT STATE (NEW)
//   const [sortConfig, setSortConfig] = useState({
//     key: null,
//     direction: "asc",
//   });

//   const role = localStorage.getItem("role"); // "admin" | "user"

//   /* FETCH ALL DATA */
//   useEffect(() => {
//     async function fetchGuards() {
//       try {
//         const res = await getAllGuard(0, 10000);
//         setGuardList(res.content || []);
//       } catch (err) {
//         toast.error("Failed to load guards!");
//       }
//     }
//     fetchGuards();
//   }, [refreshTrigger]);

//   const handleRowClick = (guard) => {
//     if (guard.status === "Active") {
//       navigate(`/officerassignedduty/${guard.id}`);
//     } else {
//       navigate(`/officerdutyhistory/${guard.id}`);
//     }
//   };

//   /* DELETE */
//   const handleDelete = async (g) => {
//     try {
//       await deleteGuard(g.id);
//       toast.success("Guard deleted successfully!");
//       setCurrentPage(0);
//     } catch (err) {
//       toast.error("Failed to delete guard!");
//     }
//   };

//   /* SORT HANDLER (NEW) */
//   const handleSort = (key) => {
//     setSortConfig((prev) => {
//       if (prev.key === key) {
//         return {
//           key,
//           direction: prev.direction === "asc" ? "desc" : "asc",
//         };
//       }
//       return { key, direction: "asc" };
//     });
//   };

//   /* FILTERING */
//   const filteredGuards = useMemo(() => {
//     return guardList.filter((g) => {
//       const validStatus = g.status === "Active" || g.status === "Inactive";
//       if (!validStatus) return false;

//       const matchStatus = selectedStatus ? g.status === selectedStatus : true;
//       const matchRank = selectedRank ? g.rank === selectedRank : true;
//       const matchSearch = searchText
//         ? Object.values(g)
//             .join(" ")
//             .toLowerCase()
//             .includes(searchText.toLowerCase())
//         : true;

//       return matchStatus && matchRank && matchSearch;
//     });
//   }, [guardList, selectedStatus, selectedRank, searchText]);

//   /* SORTING (COLUMN + DEFAULT STATUS) */
//   const sortedGuards = useMemo(() => {
//     let data = [...filteredGuards];

//     // 🔹 COLUMN SORT
//     if (sortConfig.key) {
//       data.sort((a, b) => {
//         const valA = a[sortConfig.key] ?? "";
//         const valB = b[sortConfig.key] ?? "";

//         if (typeof valA === "number" && typeof valB === "number") {
//           return sortConfig.direction === "asc" ? valA - valB : valB - valA;
//         }

//         return sortConfig.direction === "asc"
//           ? String(valA).localeCompare(String(valB))
//           : String(valB).localeCompare(String(valA));
//       });

//       return data;
//     }

//     // 🔹 DEFAULT STATUS SORT (OLD LOGIC)
//     const statusOrder = {
//       Inactive: 0,
//       Active: 1,
//       Deleted: 2,
//     };

//     return data.sort((a, b) => {
//       const aOrder = statusOrder[a.status] ?? 3;
//       const bOrder = statusOrder[b.status] ?? 3;
//       return aOrder - bOrder;
//     });
//   }, [filteredGuards, sortConfig]);

//   /* PAGINATION */
//   const totalPages = Math.ceil(sortedGuards.length / rowsPerPage);

//   const paginatedGuards = useMemo(() => {
//     const start = currentPage * rowsPerPage;
//     return sortedGuards.slice(start, start + rowsPerPage);
//   }, [sortedGuards, currentPage, rowsPerPage]);

//   const goToPage = (page) => {
//     if (page >= 0 && page < totalPages) setCurrentPage(page);
//   };

//   const getVisiblePages = () => [...Array(totalPages)].map((_, i) => i);

//   /* DYNAMIC RANK LIST */
//   const ranks = [...new Set(guardList.map((g) => g.rank || "Uncategorized"))];

//   /* SORT ICON */
//   const sortIcon = (key) =>
//     sortConfig.key === key
//       ? sortConfig.direction === "asc"
//         ? " ▲"
//         : " ▼"
//       : "";

//   return (
//     <div style={styles.page}>
//       {/* HEADER */}
//       <div style={styles.headerRow}>
//         <h2 style={styles.title}>Guard Records</h2>
//         <div style={styles.totalBox}>
//           <span style={styles.totalNumber}>{filteredGuards.length}</span>
//           <span style={styles.totalLabel}>Total Guards</span>
//         </div>
//       </div>

//       {/* FILTERS */}
//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Rank</label>
//           <select
//             value={selectedRank}
//             onChange={(e) => {
//               setSelectedRank(e.target.value);
//               setCurrentPage(0);
//             }}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             {ranks.map((r, i) => (
//               <option key={i} value={r}>
//                 {r}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Status</label>
//           <select
//             value={selectedStatus}
//             onChange={(e) => {
//               setSelectedStatus(e.target.value);
//               setCurrentPage(0);
//             }}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             <option value="Active">Active</option>
//             <option value="Inactive">Inactive</option>
//           </select>
//         </div>

//         <div style={{ ...styles.filterCard, flex: "2 1 300px" }}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={searchText}
//             onChange={(e) => {
//               setSearchText(e.target.value);
//               setCurrentPage(0);
//             }}
//             placeholder="Search..."
//             style={styles.searchInput}
//           />
//         </div>
//       </div>

//       {/* TABLE */}
//       <div style={styles.tableWrapper}>
//         <div style={styles.tableCard}>
//           <table style={styles.table}>
//             <thead>
//               <tr>
//                 <th style={styles.th}>#</th>
//                 <th style={styles.th} onClick={() => handleSort("name")}>
//                   Name{sortIcon("name")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("id")}>
//                   Id{sortIcon("id")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("email")}>
//                   Email{sortIcon("email")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("rank")}>
//                   Rank{sortIcon("rank")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("experience")}>
//                   Work Experience{sortIcon("experience")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("contactno")}>
//                   Contact{sortIcon("contactno")}
//                 </th>
//                 <th style={styles.th} onClick={() => handleSort("status")}>
//                   Status{sortIcon("status")}
//                 </th>
//                 {role !== "user" && <th style={styles.th}>Actions</th>}
//               </tr>
//             </thead>

//             <tbody>
//               {paginatedGuards.map((g, i) => (
//                 <tr
//                   key={g.id}
//                   onClick={() => handleRowClick(g)}
//                   style={{ cursor: "pointer" }}
//                 >
//                   <td style={styles.td}>{i + 1 + currentPage * rowsPerPage}</td>
//                   <td style={styles.td}>{g.name}</td>
//                   <td style={styles.td}>{g.id}</td>
//                   <td style={styles.td}>{g.email}</td>
//                   <td style={styles.td}>{g.rank}</td>
//                   <td style={styles.td}>{g.experience} Years</td>
//                   <td style={styles.td}>{g.contactno}</td>
//                   <td style={styles.td}>
//                     <span
//                       style={
//                         g.status === "Active"
//                           ? styles.statusActive
//                           : styles.statusInactive
//                       }
//                     >
//                       {g.status}
//                     </span>
//                   </td>
//                   {role !== "user" && (
//                     <td style={styles.actionCol}>
//                       <button
//                         style={styles.editBtn}
//                         onClick={(e) => {
//                           e.stopPropagation(); // 🔥 IMPORTANT
//                           handleEdit(g);
//                         }}
//                       >
//                         ✏️
//                       </button>
//                       <button
//                         style={styles.deleteBtn}
//                         onClick={(e) => {
//                           e.stopPropagation(); // 🔥 IMPORTANT
//                           handleDelete(g);
//                         }}
//                       >
//                         🗑
//                       </button>
//                     </td>
//                   )}
//                 </tr>
//               ))}
//             </tbody>
//           </table>

//           {/* PAGINATION */}
//           <div style={styles.paginationContainer}>
//             <button
//               disabled={currentPage === 0}
//               style={styles.pageBtn}
//               onClick={() => goToPage(currentPage - 1)}
//             >
//               Prev
//             </button>

//             {getVisiblePages().map((i) => (
//               <button
//                 key={i}
//                 style={{
//                   ...styles.pageBtn,
//                   ...(currentPage === i ? styles.activePage : {}),
//                 }}
//                 onClick={() => goToPage(i)}
//               >
//                 {i + 1}
//               </button>
//             ))}

//             <button
//               disabled={currentPage >= totalPages - 1}
//               style={styles.pageBtn}
//               onClick={() => goToPage(currentPage + 1)}
//             >
//               Next
//             </button>

//             <select
//               value={rowsPerPage}
//               style={styles.rowsSelect}
//               onChange={(e) => {
//                 setRowsPerPage(Number(e.target.value));
//                 setCurrentPage(0);
//               }}
//             >
//               <option value={20}>20 / Page</option>
//               <option value={30}>30 / Page</option>
//             </select>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* STYLES — UNCHANGED */
// const styles = {
//   page: {
//     minHeight: "90vh",
//     width: "100%",
//     background:
//       "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))",
//   },
//   headerRow: {
//     display: "flex",
//     justifyContent: "space-between",
//     padding: "12px 20px",
//   },
//   title: { fontSize: 26, fontWeight: 800, color: "#1967d2" },
//   totalBox: {
//     display: "flex",
//     gap: 10,
//     background: "#e8f1ff",
//     padding: "10px 18px",
//     borderRadius: 12,
//   },
//   totalNumber: { fontSize: 22, fontWeight: 800 },
//   totalLabel: { fontSize: 15, fontWeight: 600 },
//   filterRow: { display: "flex", gap: 16, padding: "0 20px 18px" },
//   filterCard: {
//     padding: 16,
//     borderRadius: 12,
//     background: "rgba(255,255,255,0.45)",
//   },
//   filterLabel: { fontWeight: 600 },
//   select: { width: "100%", padding: 10, borderRadius: 8 },
//   searchInput: { width: "100%", padding: 10, borderRadius: 8 },
//   tableWrapper: { overflowX: "auto" },
//   tableCard: {
//     margin: "0 20px",
//     background: "rgba(255,255,255,0.7)",
//     borderRadius: 12,
//     padding: 10,
//   },
//   table: { width: "100%", borderCollapse: "collapse" },
//   th: { padding: 12, background: "#b8b8f3ff", cursor: "pointer" },
//   td: { padding: 10, borderBottom: "1px solid #eee" },
//   actionCol: { display: "flex", gap: 8 },
//   editBtn: {
//     background: "#28a745",
//     color: "#fff",
//     border: 0,
//     padding: "6px 10px",
//   },
//   deleteBtn: {
//     background: "#fc4f30",
//     color: "#fff",
//     border: 0,
//     padding: "6px 10px",
//   },
//   statusActive: {
//     background: "#e6ffe6",
//     padding: "4px 10px",
//     borderRadius: 20,
//   },
//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "4px 10px",
//     borderRadius: 20,
//   },
//   paginationContainer: {
//     display: "flex",
//     gap: 10,
//     justifyContent: "center",
//     padding: 15,
//   },
//   pageBtn: { padding: "6px 14px" },
//   activePage: { background: "#1967d2", color: "#fff" },
//   rowsSelect: { padding: "6px 14px" },
// };
